import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import { ChevronDown } from "lucide-react";
import type { JSX } from "react";

import { CardGrid, CheckList, CtaBand, PageHero, Section, SectionHeading, Timeline } from "./blocks.js";
import { COMMITMENTS, ELIGIBILITY, FAQ, ONBOARDING, PAGE_META, PROGRAM_INTRO, PUBLIC_ROUTES } from "./content.js";
import { usePageMeta } from "./usePageMeta.js";

/**
 * `/reseller-program` (spec E, Task 7, as revised 2026-09-08 afternoon).
 *
 * The first version of this page was the landing page again in longer
 * words: the same four benefit cards, the same four steps, the same six
 * portal tiles. The product owner's note — "make reseller program not copy
 * the same info what home page have" — is the reason for the body below.
 *
 * The split now: `/` makes the case (why partner, what you get), and this
 * page answers what somebody reads a program page to decide — do we
 * qualify, what will onboarding actually ask of us, what are both sides
 * signing, and the questions left over. None of its four sections appears
 * on the home page, and the arrays it renders (`ELIGIBILITY`, `ONBOARDING`,
 * `COMMITMENTS`) are read from nowhere else.
 */
export function ResellerProgramPage(): JSX.Element {
  usePageMeta(PAGE_META.program);

  return (
    <>
      <PageHero title="The XOP Networks Reseller Program" lede={PROGRAM_INTRO} />

      {/* ── Who the program is for */}
      <Section id="eligibility">
        <SectionHeading
          id="eligibility"
          title="Who the program is for"
          lede="Four things XOP Networks looks for in an application. Meet them and the rest is paperwork."
        />
        <CheckList items={ELIGIBILITY} />
      </Section>

      {/* ── How onboarding works */}
      <Section id="onboarding" tone="soft">
        <SectionHeading
          id="onboarding"
          title="How onboarding works"
          lede="Four stages — three of them yours, one of them XOP Networks'. Each one ends with something in your inbox."
        />
        <Timeline aria-label="How onboarding works" items={ONBOARDING} />
      </Section>

      {/* ── What each side commits to */}
      <Section id="commitments">
        <SectionHeading
          id="commitments"
          title="What each side commits to"
          lede="The Reseller Agreement in plain words, both ways round."
        />
        <CardGrid columns={2} items={COMMITMENTS.map((c) => ({ title: c.title, items: c.items }))} />
      </Section>

      {/* ── FAQ */}
      <Section id="faq" tone="soft">
        <SectionHeading id="faq" title="Frequently asked questions" />
        {FAQ.map((item, i) => (
          <Accordion
            key={item.question}
            disableGutters
            elevation={0}
            sx={{ bgcolor: "transparent", borderBottom: 1, borderColor: "divider", "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ChevronDown size={18} />} aria-controls={`faq-${i}-content`} id={`faq-${i}-header`}>
              <Typography component="h3" sx={{ fontWeight: 600 }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails id={`faq-${i}-content`}>
              <Typography sx={{ color: "text.secondary" }}>{item.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Section>

      <CtaBand
        id="cta"
        title="Apply in five minutes"
        body="No fee, no password yet — we email your next step."
        primary={{ label: "Apply now", href: PUBLIC_ROUTES.register }}
        secondary={{ label: "Sign in", href: PUBLIC_ROUTES.login }}
      />
    </>
  );
}
